import {
  collection,
  collectionGroup,
  doc,
  addDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  increment,
  arrayUnion,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import type { PostVisibility, MatchVenue, MatchType, ChannelVisibility } from "./types";

// ── Club Creation ─────────────────────────────────────

export async function createClub(data: {
  name: string;
  shortName: string;
  teams: string[];
  userId: string;
  userEmail: string;
  userName: string;
}) {
  // Feature flag — club creation disabled until launch
  const SUPER_ADMINS = ["veer25mangat@gmail.com"];
  const normalised = data.userEmail.toLowerCase().replace(/\+.*@/, "@");
  const isSuperAdmin = SUPER_ADMINS.includes(normalised);

  if (!isSuperAdmin) {
    // Check feature flag from Firestore
    const flagDoc = await getDoc(doc(db, "config", "features"));
    const flags = flagDoc.exists() ? flagDoc.data() : {};
    if (!flags.clubCreationEnabled) {
      throw new Error("Club creation is not available yet. Join the waitlist at inthecrease.uk");
    }
  }

  // Prevent duplicate club names
  const existingClubs = await getDocs(
    query(
      collection(db, "clubs"),
      where("name", "==", data.name)
    )
  );

  if (!existingClubs.empty) {
    throw new Error("A club with this name already exists. Please choose a different name.");
  }

  // Create club doc
  const clubRef = await addDoc(collection(db, "clubs"), {
    name: data.name,
    shortName: data.shortName,
    teams: data.teams,
    createdBy: data.userId,
    createdAt: Timestamp.now(),
  });

  const clubId = clubRef.id;

  // Create admin member doc
  await setDoc(doc(db, "clubs", clubId, "members", data.userId), {
    userId: data.userId,
    displayName: data.userName,
    email: data.userEmail,
    role: "admin",
    teams: data.teams,
    clubId,
    clubName: data.name,
    profileComplete: false,
    joinedAt: Timestamp.now(),
  });

  // Add clubId to user doc
  await updateDoc(doc(db, "users", data.userId), {
    clubIds: arrayUnion(clubId),
  });

  return clubId;
}


// ── Posts ──────────────────────────────────────────────

export async function createPost(
  clubId: string,
  data: {
    authorId: string;
    authorName: string;
    authorRole: string;
    text: string;
    visibility: PostVisibility;
    clubName: string;
  }
) {
  return addDoc(collection(db, "clubs", clubId, "posts"), {
    ...data,
    imageUrls: [],
    likeCount: 0,
    commentCount: 0,
    clubId,
    createdAt: Timestamp.now(),
  });
}

export async function deletePost(clubId: string, postId: string) {
  return deleteDoc(doc(db, "clubs", clubId, "posts", postId));
}

export async function toggleLike(
  clubId: string,
  postId: string,
  userId: string,
  displayName: string,
  isLiked: boolean
) {
  const likeRef = doc(db, "clubs", clubId, "posts", postId, "likes", userId);
  const postRef = doc(db, "clubs", clubId, "posts", postId);

  if (isLiked) {
    await deleteDoc(likeRef);
    await updateDoc(postRef, { likeCount: increment(-1) });
  } else {
    await setDoc(likeRef, { displayName, createdAt: Timestamp.now() });
    await updateDoc(postRef, { likeCount: increment(1) });
  }
}

// ── Matches ────────────────────────────────────────────

export async function createMatch(
  clubId: string,
  data: {
    team: string;
    opponent: string;
    date: string;
    time: string;
    venue: MatchVenue;
    type: MatchType;
    location?: string;
    clubName: string;
    createdBy: string;
    availabilityLeadDays?: number;
  }
) {
  const lead = data.availabilityLeadDays ?? 7;
  const matchDate = new Date(data.date + "T00:00:00");
  matchDate.setDate(matchDate.getDate() - lead);
  const opensAt = matchDate.toISOString().slice(0, 10);
  const { availabilityLeadDays, ...rest } = data;
  return addDoc(collection(db, "clubs", clubId, "matches"), {
    ...rest,
    location: data.location || null,
    availabilityOpensAt: opensAt,
    availabilityNotifiedAt: null,
    status: "availability",
    actingCaptainId: null,
    actingCaptainName: null,
    proposedBy: null,
    confirmedBy: null,
    clubId,
    createdAt: Timestamp.now(),
  });
}

export async function updateMatch(
  clubId: string,
  matchId: string,
  data: {
    opponent?: string;
    date?: string;
    time?: string;
    venue?: MatchVenue;
    type?: MatchType;
    location?: string | null;
  }
) {
  // Filter out undefined values — Firestore doesn't accept them
  const clean: Record<string, any> = {};
  for (const [k, v] of Object.entries(data)) {
    if (v !== undefined) clean[k] = v;
  }
  return updateDoc(doc(db, "clubs", clubId, "matches", matchId), clean);
}

export async function setAvailability(
  clubId: string,
  matchId: string,
  data: {
    userId: string;
    displayName: string;
    status: "available" | "unavailable" | "maybe";
    note: string;
    driving?: boolean;
    clubId: string;
    clubName: string;
    matchDate: string;
    team: string;
  }
) {
  return setDoc(
    doc(db, "clubs", clubId, "matches", matchId, "availability", data.userId),
    {
      ...data,
      driving: data.driving ?? false,
      selected: false,
      respondedAt: Timestamp.now(),
    }
  );
}

export async function setDayAvailability(
  clubId: string,
  matchIds: string[],
  data: {
    userId: string;
    displayName: string;
    status: "available" | "unavailable" | "maybe";
    note: string;
    driving?: boolean;
    clubId: string;
    clubName: string;
    matchDate: string;
  }
) {
  const writes = matchIds.map((matchId) =>
    setDoc(
      doc(db, "clubs", clubId, "matches", matchId, "availability", data.userId),
      {
        ...data,
        driving: data.driving ?? false,
        team: "",
        selected: false,
        respondedAt: Timestamp.now(),
      }
    )
  );
  await Promise.all(writes);
}

// ── Channels ───────────────────────────────────────────

export async function createChannel(
  clubId: string,
  data: {
    name: string;
    emoji: string;
    visibility: ChannelVisibility;
    createdBy: string;
    createdByName: string;
  }
) {
  const channelRef = await addDoc(collection(db, "clubs", clubId, "channels"), {
    name: data.name,
    emoji: data.emoji,
    type: "chat",
    visibility: data.visibility,
    createdBy: data.createdBy,
    memberCount: 1,
    lastMessage: null,
    lastMessageAt: null,
    clubId,
    createdAt: Timestamp.now(),
  });

  // Add creator as channel admin
  await setDoc(
    doc(db, "clubs", clubId, "channels", channelRef.id, "members", data.createdBy),
    {
      userId: data.createdBy,
      displayName: data.createdByName,
      role: "admin",
      status: "active",
      joinedAt: Timestamp.now(),
    }
  );

  return channelRef.id;
}

export async function sendMessage(
  clubId: string,
  channelId: string,
  data: {
    text: string;
    authorId: string;
    authorName: string;
  }
) {
  const now = Timestamp.now();
  const msgRef = await addDoc(
    collection(db, "clubs", clubId, "channels", channelId, "messages"),
    {
      ...data,
      type: "text",
      createdAt: now,
    }
  );

  // Update channel last message
  await updateDoc(doc(db, "clubs", clubId, "channels", channelId), {
    lastMessage: data.text,
    lastMessageAt: now,
  });

  // Mark sender as read so their own channel row doesn't show as unread
  try {
    await setDoc(
      doc(db, "clubs", clubId, "channels", channelId, "members", data.authorId),
      { lastReadAt: now },
      { merge: true }
    );
  } catch {}

  return msgRef;
}

// ── Withdrawals ─────────────────────────────────────

export async function withdrawFromMatch(
  clubId: string,
  matchId: string,
  userId: string,
  reason: string,
) {
  const selRef = doc(db, "clubs", clubId, "matches", matchId, "selections", userId);
  await updateDoc(selRef, {
    withdrawn: true,
    withdrawnAt: Timestamp.now(),
    withdrawalReason: reason,
  });

  // If withdrawing before match start and fee is unpaid, clear it
  try {
    const matchSnap = await getDoc(doc(db, "clubs", clubId, "matches", matchId));
    if (matchSnap.exists()) {
      const m = matchSnap.data() as any;
      const startsAt = new Date(`${m.date}T${m.time || "00:00"}:00`);
      if (startsAt.getTime() > Date.now()) {
        const feeRef = doc(db, "clubs", clubId, "matches", matchId, "fees", userId);
        const feeSnap = await getDoc(feeRef);
        if (feeSnap.exists() && !feeSnap.data()?.paid) {
          await deleteDoc(feeRef);
        }
      }
    }
  } catch {}
}

// ── Channels ───────────────────────────────────────────

export async function requestChannelJoin(
  clubId: string,
  channelId: string,
  userId: string,
  displayName: string
) {
  return setDoc(
    doc(db, "clubs", clubId, "channels", channelId, "members", userId),
    {
      userId,
      displayName,
      role: "member",
      status: "pending",
      joinedAt: Timestamp.now(),
    }
  );
}

// ── Trainings ────────────────────────────────────────────

function ymd(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export async function createTrainingSeries(
  clubId: string,
  data: {
    title: string;
    dayOfWeek: number;
    time: string;
    duration: number;
    startDate: string;
    endDate: string;
    location: string;
    fee: number;
    clubName: string;
    createdBy: string;
    availabilityLeadDays?: number;
  }
) {
  const seriesRef = await addDoc(collection(db, "clubs", clubId, "trainingSeries"), {
    title: data.title,
    dayOfWeek: data.dayOfWeek,
    time: data.time,
    duration: data.duration,
    startDate: data.startDate,
    endDate: data.endDate,
    location: data.location,
    fee: data.fee,
    clubId,
    clubName: data.clubName,
    createdBy: data.createdBy,
    createdAt: Timestamp.now(),
  });

  const lead = data.availabilityLeadDays ?? 7;
  const start = new Date(data.startDate + "T00:00:00");
  const end = new Date(data.endDate + "T00:00:00");

  // Move start forward to first matching dayOfWeek
  while (start.getDay() !== data.dayOfWeek) start.setDate(start.getDate() + 1);

  const writes: Promise<any>[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const date = ymd(cursor);
    const opens = new Date(cursor);
    opens.setDate(opens.getDate() - lead);
    writes.push(
      addDoc(collection(db, "clubs", clubId, "trainings"), {
        seriesId: seriesRef.id,
        title: data.title,
        date,
        time: data.time,
        duration: data.duration,
        location: data.location,
        fee: data.fee,
        cancelled: false,
        availabilityOpensAt: ymd(opens),
        clubId,
        clubName: data.clubName,
        createdAt: Timestamp.now(),
      })
    );
    cursor.setDate(cursor.getDate() + 7);
  }

  await Promise.all(writes);
  return seriesRef.id;
}

export async function setTrainingAttendance(
  clubId: string,
  trainingId: string,
  data: {
    userId: string;
    displayName: string;
    status: "in" | "out";
    trainingDate: string;
    trainingTitle: string;
    fee: number;
  }
) {
  const ref = doc(db, "clubs", clubId, "trainings", trainingId, "attendance", data.userId);
  await setDoc(ref, {
    userId: data.userId,
    displayName: data.displayName,
    status: data.status,
    clubId,
    trainingId,
    trainingDate: data.trainingDate,
    respondedAt: Timestamp.now(),
  });

  // Manage fee doc
  const feeRef = doc(db, "clubs", clubId, "trainings", trainingId, "fees", data.userId);
  if (data.status === "in" && data.fee > 0) {
    await setDoc(feeRef, {
      userId: data.userId,
      displayName: data.displayName,
      amount: data.fee,
      paid: false,
      paidAt: null,
      markedBy: null,
      feeType: "training",
      clubId,
      clubName: "",
      matchDate: data.trainingDate,
      matchName: data.trainingTitle,
      team: "",
      createdAt: Timestamp.now(),
    });
  } else {
    // Remove fee if user opts out and it isn't already paid
    try {
      const snap = await getDoc(feeRef);
      if (snap.exists() && !snap.data()?.paid) {
        await deleteDoc(feeRef);
      }
    } catch {}
  }
}

// Delete a single training session along with its unpaid fees and attendance.
// Paid fees are preserved (orphaned under collectionGroup) for audit/history.
export async function cancelTrainingSession(clubId: string, trainingId: string) {
  const feesSnap = await getDocs(collection(db, "clubs", clubId, "trainings", trainingId, "fees"));
  await Promise.all(
    feesSnap.docs.map(async (f) => {
      if (!f.data()?.paid) await deleteDoc(f.ref);
    })
  );
  const attSnap = await getDocs(collection(db, "clubs", clubId, "trainings", trainingId, "attendance"));
  await Promise.all(attSnap.docs.map((a) => deleteDoc(a.ref)));
  await deleteDoc(doc(db, "clubs", clubId, "trainings", trainingId));
}

export async function deleteTrainingSeries(clubId: string, seriesId: string, mode: "future" | "all") {
  const today = ymd(new Date());
  const trainingsSnap = await getDocs(
    query(collection(db, "clubs", clubId, "trainings"), where("seriesId", "==", seriesId))
  );
  for (const d of trainingsSnap.docs) {
    const data = d.data() as any;
    if (mode === "all" || data.date >= today) {
      await cancelTrainingSession(clubId, d.id);
    }
  }
  if (mode === "all") {
    await deleteDoc(doc(db, "clubs", clubId, "trainingSeries", seriesId));
  }
}
